// Creator listings: create, edit, publish.
//
// A listing is created as a draft directly through Supabase (row-level security
// scopes it to the creator), but publishing goes through the server, because
// publishing is what mints the Stripe Product and Price and is therefore not
// something a client should be able to assert for itself.
//
// The payout gate is surfaced before the form rather than after. Discovering
// that you cannot sell only once you have written a description and set a price
// is the kind of small cruelty that makes people abandon a flow.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleCheck, Eye, Image as ImageIcon, Loader, Package, Pencil, Plus, Store, Trash2, TriangleAlert } from 'lucide-react';
import ListingEditor from '../components/marketplace/ListingEditor';
import PageHeader from '../components/PageHeader';
import { MARKET_LINKS } from './Marketplace';
import { supabase } from '../config/supabase';
import AssetPicker, { PickedAsset } from '../components/AssetPicker';
import { putObject } from '../utils/uploadToStorage';
import { gathrxrAuthHeader } from '../utils/quotaHeaders';
import { MARKETPLACE_PURCHASES_ENABLED, MARKETPLACE_LISTED_MESSAGE, MARKETPLACE_MIN_PRICE_CENTS, MARKETPLACE_DESCRIPTION_MAX, MARKETPLACE_TITLE_MAX } from '../config/marketplace';
import './Marketplace.css';

interface Listing {
  id: string;
  asset_url?: string | null;
  world_id?: string | null;
  title: string;
  description: string | null;
  kind: string;
  price_cents: number;
  currency: string;
  status: string;
  sales_count: number;
  thumbnail_url: string | null;
  sale_type?: 'copy' | 'original';
  source_id?: string | null;
}

const KINDS = ['model', 'world', 'prefab', 'texture', 'avatar'];
const money = (c: number) => `$${(c / 100).toFixed(2)}`;

export default function CreatorListings() {
  const navigate = useNavigate();
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [payouts, setPayouts] = useState<{ enabled: boolean; connected: boolean } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');
  // Title of the listing that just went live, for the "listed — sales open
  // soon" note while marketplace purchases are switched off.
  const [justListed, setJustListed] = useState<string | null>(null);
  const [draft, setDraft] = useState({
    title: '', description: '', kind: 'model', price: '4.99',
    saleType: 'copy' as 'copy' | 'original',
  });
  const [creating, setCreating] = useState(false);
  // Listing open in the inline editor, and the last save confirmation.
  const [editing, setEditing] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [asset, setAsset] = useState<PickedAsset | null>(null);
  const [thumbBusy, setThumbBusy] = useState(false);
  const thumbRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('marketplace_listings')
      .select('*')
      .order('created_at', { ascending: false });
    setListings((data as Listing[]) || []);

    try {
      const headers = await gathrxrAuthHeader();
      if (headers['x-gathrxr-token']) {
        const res = await fetch('/api/marketplace/connect', { headers });
        const d = await res.json();
        setPayouts({ enabled: !!d?.payoutsEnabled, connected: !!d?.connected });
      }
    } catch { /* the banner just stays hidden */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const pick = (a: PickedAsset) => {
    setAsset(a);
    setPicking(false);
    // Prefill only what is still empty. Overwriting a title the creator has
    // already written is the kind of "helpful" that loses work.
    setDraft((d) => ({
      ...d,
      title: d.title.trim() ? d.title : a.title,
      kind: a.kind,
      // A file uploaded straight to storage has no library record, so there is
      // nothing to hand over. Fall back to a copy rather than offering a
      // transfer that cannot complete.
      saleType: a.sourceId ? d.saleType : 'copy',
    }));
  };

  /** Manual thumbnail, for sources that have none — an upload, mostly. */
  const uploadThumb = async (file: File) => {
    setThumbBusy(true);
    setError('');
    try {
      const key = `marketplace/thumbs/${Date.now()}-${file.name.replace(/\s+/g, '-')}`;
      const url = await putObject(key, file, file.type);
      setAsset((a) => (a ? { ...a, thumbnailUrl: url } : a));
    } catch (e: any) {
      setError(e?.message || 'Thumbnail upload failed.');
    } finally {
      setThumbBusy(false);
    }
  };

  const create = async () => {
    setError('');
    const cents = Math.round(parseFloat(draft.price || '0') * 100);
    if (!draft.title.trim()) return setError('Give it a title.');
    // A listing with nothing attached is a price with a name on it. The buyer
    // would pay and receive nothing, so this is blocked here rather than
    // discovered after a sale.
    if (!asset || (!asset.assetUrl && !asset.worldId)) {
      return setError('Choose what you are selling.');
    }
    // Matches the server check, so the failure lands here next to the field
    // rather than at checkout in front of a buyer.
    if (!cents || cents < MARKETPLACE_MIN_PRICE_CENTS) return setError(`Price must be at least $${(MARKETPLACE_MIN_PRICE_CENTS / 100).toFixed(2)}.`);

    setCreating(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setError('Sign in first.'); setCreating(false); return; }

    const { error: e } = await supabase.from('marketplace_listings').insert({
      creator_id: user.id,
      title: draft.title.trim(),
      description: draft.description.trim() || null,
      kind: draft.kind,
      price_cents: cents,
      asset_url: asset.assetUrl,
      world_id: asset.worldId,
      thumbnail_url: asset.thumbnailUrl,
      sale_type: draft.saleType,
      source_kind: asset.sourceKind,
      source_id: asset.sourceId,
      status: 'draft',
    });
    if (e) setError(e.message);
    else {
      setDraft({ title: '', description: '', kind: 'model', price: '4.99', saleType: 'copy' });
      setAsset(null);
    }
    setCreating(false);
    load();
  };

  const publish = async (id: string) => {
    setBusy(id);
    setError('');
    setJustListed(null);
    try {
      const headers = await gathrxrAuthHeader();
      const res = await fetch('/api/marketplace/publish', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ listingId: id }),
      });
      const d = await res.json();
      if (!res.ok) setError(d?.error || 'Could not publish.');
      else {
        if (!MARKETPLACE_PURCHASES_ENABLED) {
          setJustListed(listings.find((l) => l.id === id)?.title || 'Your item');
        }
        load();
      }
    } catch {
      setError('Could not reach the server.');
    } finally {
      setBusy(null);
    }
  };

  const unpublish = async (id: string) => {
    setBusy(id);
    await supabase.from('marketplace_listings').update({ status: 'draft' }).eq('id', id);
    setBusy(null);
    load();
  };

  // Drafts only. A published listing must be unpublished first, which keeps
  // "delete" from doubling as an instant take-down on something buyers may be
  // mid-checkout on; past sales survive either way (purchases keep their own
  // copy of the price and null out listing_id on delete).
  const remove = async (l: Listing) => {
    if (!window.confirm(`Delete "${l.title}"? This cannot be undone.`)) return;
    setBusy(l.id);
    setError('');
    const { error } = await supabase.from('marketplace_listings').delete().eq('id', l.id);
    if (error) setError(`Could not delete: ${error.message}`);
    setBusy(null);
    load();
  };

  return (
    <div className="mk-page mk-page-hero">
      <PageHeader
        title="Your listings"
        accent="Sell what you build."
        subtitle="List the models, props, avatars and worlds you've made. You keep 80% of every sale."
        hero="/hero-marketplace.webp"
        links={MARKET_LINKS}
        actions={
          <button className="mk-ghost" onClick={() => navigate('/marketplace')}>
            <Store size={14} /> View marketplace
          </button>
        }
      />

      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        {payouts && !payouts.enabled && (
          <div className="mk-banner">
            <TriangleAlert size={17} />
            <div>
              <strong>Payouts aren't set up yet</strong>
              <p>
                You can publish now and your items will show in the marketplace,
                but nobody can buy them until you connect a payout account —
                otherwise there's no way to pay you.
              </p>
            </div>
            <button className="mk-primary" onClick={() => navigate('/creator/payouts')}>
              {payouts.connected ? 'Finish setup' : 'Set up payouts'}
            </button>
          </div>
        )}

        {justListed && (
          <div className="mk-banner mk-banner-soon" role="status">
            <CircleCheck size={17} />
            <div>
              <strong>“{justListed}” is listed</strong>
              <p>{MARKETPLACE_LISTED_MESSAGE}</p>
            </div>
            <button className="mk-ghost" onClick={() => navigate('/marketplace')}>
              <Store size={14} /> See it live
            </button>
            <button className="mk-banner-x" onClick={() => setJustListed(null)} aria-label="Dismiss">×</button>
          </div>
        )}

        <section className="mk-form">
          <h2><Plus size={16} /> New listing</h2>

          {/* The asset comes first. Filling in a price for a thing you have not
              chosen yet is backwards, and it is the step people forget. */}
          {asset ? (
            <div className="mk-asset">
              <span className="mk-asset-thumb">
                {asset.thumbnailUrl
                  ? <img src={asset.thumbnailUrl} alt="" />
                  : <Package size={18} />}
              </span>
              <div className="mk-asset-meta">
                <strong>{asset.title}</strong>
                <span>{asset.worldId ? 'World' : asset.kind}</span>
                {!asset.thumbnailUrl && (
                  <span className="mk-asset-warn">No thumbnail — buyers see a grey box</span>
                )}
              </div>
              <input
                ref={thumbRef}
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadThumb(f); }}
              />
              <button className="mk-ghost" onClick={() => thumbRef.current?.click()} disabled={thumbBusy}>
                {thumbBusy ? <Loader size={13} className="spin" /> : <ImageIcon size={13} />}
                {asset.thumbnailUrl ? 'Replace image' : 'Add image'}
              </button>
              <button className="mk-ghost" onClick={() => setPicking(true)}>Change</button>
            </div>
          ) : (
            <button className="mk-choose" onClick={() => setPicking(true)}>
              <Package size={17} />
              <span>
                <strong>Choose what you're selling</strong>
                <em>A world, something from your library, an avatar, or a new upload</em>
              </span>
            </button>
          )}

          {asset && (
            <div className="mk-saletype">
              <button
                type="button"
                className={`mk-sale ${draft.saleType === 'copy' ? 'on' : ''}`}
                onClick={() => setDraft({ ...draft, saleType: 'copy' })}
              >
                <strong>Sell copies</strong>
                <em>Buyers get their own copy. You keep yours and can sell it any number of times.</em>
              </button>
              <button
                type="button"
                className={`mk-sale ${draft.saleType === 'original' ? 'on' : ''} ${!asset.sourceId ? 'off' : ''}`}
                onClick={() => asset.sourceId && setDraft({ ...draft, saleType: 'original' })}
                disabled={!asset.sourceId}
                title={asset.sourceId ? '' : 'Uploaded files can only be sold as copies'}
              >
                <strong>Sell the original</strong>
                <em>
                  {asset.sourceId
                    ? 'One sale only. Ownership transfers to the buyer and it leaves your account.'
                    : 'Not available for direct uploads — add it to your library first.'}
                </em>
              </button>
            </div>
          )}

          {draft.saleType === 'original' && (
            <p className="mk-sale-warn">
              <TriangleAlert size={14} />
              When this sells, <strong>{asset?.title}</strong> moves to the buyer and
              is removed from your account. This cannot be undone from here.
            </p>
          )}

          <div className="mk-fields">
            <label>
              <span>Title</span>
              <input
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="Neon arcade cabinet"
                maxLength={MARKETPLACE_TITLE_MAX}
              />
            </label>
            <label>
              <span>Type</span>
              <select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })}>
                {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </label>
            <label>
              <span>Price (USD)</span>
              <input
                type="number" min="0.50" step="0.50"
                value={draft.price}
                onChange={(e) => setDraft({ ...draft, price: e.target.value })}
              />
            </label>
          </div>
          <label className="mk-full">
            <span>Description</span>
            <textarea
              rows={3}
              maxLength={MARKETPLACE_DESCRIPTION_MAX}
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="What it is, and what someone would use it for."
            />
          </label>
          <div className="mk-form-foot">
            {/* Earnings shown before the sale, not after. 80% of a number the
                creator just typed is more useful than a percentage in the
                terms page. */}
            <span className="mk-hint">
              You receive {money(Math.round((parseFloat(draft.price || '0') * 100) * 0.8))} per sale
            </span>
            <button className="mk-primary" onClick={create} disabled={creating}>
              {creating ? <Loader size={14} className="spin" /> : <Plus size={14} />}
              Create draft
            </button>
          </div>
        </section>

        {error && <p className="mk-error">{error}</p>}

        {saved && (
          <div className="mk-banner mk-banner-ok" role="status" style={{ marginTop: 16 }}>
            <CircleCheck size={17} />
            <div><strong>{saved}</strong></div>
            <button className="mk-banner-x" onClick={() => setSaved(null)} aria-label="Dismiss">×</button>
          </div>
        )}

        {loading && <div className="mk-state"><Loader size={16} className="spin" /> Loading…</div>}

        {!loading && !listings.length && (
          <div className="mk-empty">
            <Package size={26} />
            <h2>No listings yet</h2>
            <p>Create a draft above. Nothing goes live until you publish it.</p>
          </div>
        )}

        {!loading && listings.length > 0 && (
          <ul className="mk-list">
            {listings.map((l) => (
              <li key={l.id} className={`mk-row ${editing === l.id ? 'mk-row-edit' : ''}`}>
                <div className="mk-row-main">
                  <span className="mk-row-thumb">
                    {l.thumbnail_url ? <img src={l.thumbnail_url} alt="" loading="lazy" /> : <Package size={15} />}
                  </span>
                  <span className={`mk-status mk-status-${l.status}`}>{l.status}</span>
                  <div>
                    <h3>{l.title}</h3>
                    <p>
                      {l.kind} · {money(l.price_cents)} · {l.sales_count} sold
                      {(l as any).sale_type === 'original' && ' · one-of-one'}
                      {l.status === 'published' && !MARKETPLACE_PURCHASES_ENABLED && ' · sales open soon'}
                    </p>
                  </div>
                </div>
                <div className="mk-row-actions">
                  {l.status !== 'sold' && editing !== l.id && (
                    <button className="mk-ghost" onClick={() => { setEditing(l.id); setSaved(null); }} disabled={busy === l.id}>
                      <Pencil size={13} /> Edit
                    </button>
                  )}
                  {l.status === 'published' && l.kind === 'world' && (
                    <button className="mk-ghost" onClick={() => navigate(`/marketplace/preview/${l.id}`)} title="See what buyers see">
                      <Eye size={13} /> Preview
                    </button>
                  )}
                  {l.status === 'published' ? (
                    <button className="mk-ghost" onClick={() => unpublish(l.id)} disabled={busy === l.id}>
                      Unpublish
                    </button>
                  ) : (
                    <>
                      <button
                        className="mk-primary"
                        onClick={() => publish(l.id)}
                        disabled={busy === l.id}
                      >
                        {busy === l.id ? <Loader size={13} className="spin" /> : null}
                        Publish
                      </button>
                      <button
                        className="mk-ghost mk-danger"
                        onClick={() => remove(l)}
                        disabled={busy === l.id}
                        title="Delete this draft"
                      >
                        <Trash2 size={13} />
                      </button>
                    </>
                  )}
                </div>
                {editing === l.id && (
                  <ListingEditor
                    listing={l}
                    onCancel={() => setEditing(null)}
                    onSaved={(msg) => { setEditing(null); setSaved(msg); load(); }}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {picking && <AssetPicker onPick={pick} onClose={() => setPicking(false)} />}
    </div>
  );
}
